import { Box, Grid, LinearProgress } from "@mui/material";
import { ReactComponent as DeleteIcon } from "assets/DeleteIcon.svg";
import { ReactComponent as EditIcon } from "assets/EditIcon.svg";
import { ReactComponent as Ellipse } from "assets/Ellipse.svg";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import Chip from "components/common/Chip";
import FunctionalAreaChips from "components/common/FunctionalAreaChips";
import KebabMenu from "components/common/KebabMenu";
import PlanCard from "components/common/PlanCard";
import PlanType from "components/common/PlanType";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import ToolTip from "components/common/Tooltip";
import Typography from "components/common/Typography";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";
import { accessTypes, brandDeadLine, messageTypes } from "utils/CommonConstants";
import { getSourceName, matchOwnerWithHltId } from "utils/CommonFunctions";
import { getUIFormatDate } from "utils/DateHelper";
import { planTypes } from "utils/Types";
import { fetchMyPlanDetailAsync } from "../MyPlanDetails/MyPlanDetails.slice";
import Delete from "./Delete";
import Edit from "./Edit";

interface Props {
  plan: any;
}

function Item(props: Props) {
  const navigate = useNavigate();
  const dispatch: any = useAppDispatch();
  const { plan } = props;
  const [openEditPlan, setOpenEditPlan] = useState(false);
  const [openDeleteCapthaDialog, setOpenDeleteCapthaDialog] = useState(Boolean);
  const ownerDetails: any = useAppSelector((state) => state.myplans.MyPlans.ownerDetails);
  const isAdmin = useAppSelector((state) => state.app.isAdmin);

  const { isMobileScreen } = useContext(ThemeContext);

  const SectionMenu = [
    {
      label: "Edit Plan",
      icon: EditIcon,
      enabled: true,
      accessType: accessTypes.update,
      action: async () => {
        await dispatch(fetchMyPlanDetailAsync({ id: plan?.id }));
        setOpenEditPlan(true);
      }
    },
    {
      label: "Delete Plan",
      icon: DeleteIcon,
      enabled: true,
      accessType: accessTypes.delete,
      action: () => {
        if (!isAdmin && !matchOwnerWithHltId(plan?.owners)) {
          dispatch(
            setFeedbackDetails({
              shouldShow: true,
              type: "plan",
              displayName: plan?.owners
                ?.filter((item: any) => mapOwnerInfo(item))
                .map((item: any) => mapOwnerInfo(item))
                .join(", "),
              setState: setFeedbackDetails({ shouldShow: false }),
              messageType: plan?.owners.length > 1 ? messageTypes.multipleOwners : messageTypes.singleOwner
            })
          );
          return;
        }
        setOpenDeleteCapthaDialog(true);
      }
    }
  ];

  const mapOwnerInfo = (ownerCode: string) => {
    return ownerDetails?.filter((item: any) => item?.hltId === ownerCode)[0]?.displayName;
  };

  const getPlanTypeLabelElement = () => {
    return (
      plan?.planType && (
        <PlanType
          planType={plan.planType === brandDeadLine ? 1 : 2}
          brandDeadlineDate={plan.brandDeadlineDate}
          initiativeName={plan.initiativeName}
        />
      )
    );
  };

  const getlinearProgress = () => {
    return (
      <>
        <LinearProgress
          sx={{ width: isMobileScreen ? "none" : "80px" }}
          variant="determinate"
          value={plan.totalCards > 0 ? (plan.cardsCompleted / plan.totalCards) * 100 : 0}
        />

        <Typography variant="label" color={tokens.Text.General.Tertiary}>
          {`${plan.cardsCompleted}/${plan.totalCards} done`}
        </Typography>
      </>
    );
  };

  const getPlanOwners = () => {
    const list = plan?.owners?.filter((item: any) => mapOwnerInfo(item)).map((item: any) => mapOwnerInfo(item));

    return (
      <Box
        component={isMobileScreen ? "div" : "span"}
        sx={{
          cursor: list?.length > 1 ? "pointer" : ""
        }}
      >
        <Typography variant={"label"} color={tokens.Text.General.Tertiary}>
          {` Owners: `}
        </Typography>
        <Typography variant={"label"} color={tokens.Text.General.Tertiary} showBold>
          {list?.length > 0 &&
            (plan?.owners?.length > 1 ? (
              <ToolTip title={list.length > 1 ? list?.join(", ") : list[0]}>
                <Box component={"span"}>
                  {list[0]} {`+ ${list.length - 1}`}
                </Box>
              </ToolTip>
            ) : (
              list[0]
            ))}
          {!list?.length && "-"}
        </Typography>
      </Box>
    );
  };

  const getPlanSource = () => {
    return plan?.planSource ? (
      <>
        {!isMobileScreen && (
          <Box component={"span"} alignItems={"center"} marginX={"8px"}>
            <Ellipse />
          </Box>
        )}
        <Box component={isMobileScreen ? "div" : "span"}>
          <Typography variant={"label"} color={tokens.Text.General.Tertiary}>
            {`Source: `}
          </Typography>
          <Typography variant={"label"} color={tokens.Text.General.Tertiary} showBold>
            {getSourceName(plan?.planSource)}
          </Typography>
        </Box>
      </>
    ) : (
      <></>
    );
  };

  return (
    <>
      <PlanCard type={planTypes.MyPlans}>
        {isMobileScreen ? (
          <>
            <Grid item xs={12}>
              {getPlanTypeLabelElement()}
            </Grid>
            <Grid item xs={10}>
              <Typography variant="body1" changeVariant={false} color={colors.Neutral.Neutral800} showBold>
                {plan.title || "-"}
              </Typography>
            </Grid>
            <Grid item xs={2} display="flex" justifyContent="flex-end">
              <KebabMenu options={SectionMenu} />
            </Grid>
            <Grid item xs={12} display="flex" alignItems="center">
              <FunctionalAreaChips plan={plan} />
            </Grid>
            <Grid item xs={12} display="block" alignItems="center">
              {getPlanOwners()} {getPlanSource()}
            </Grid>
            <Grid item xs={6} display="flex" alignItems="center">
              <Chip label={plan.innCode} />
            </Grid>
            <Grid item xs={6} display="flex" alignItems="center">
              <Box marginLeft="auto">
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/myplandetails/${plan?.id}`)}
                  label="View Details"
                />
              </Box>
            </Grid>
            <Grid container marginTop="15px" gap={1}>
              <Grid
                item
                display="flex"
                xs={6}
                justifyContent={"center"}
                alignItems="center"
                flexDirection={"column"}
                paddingX="12px"
                paddingY={"8px"}
                sx={{ backgroundColor: "#F7F8F9" }}
              >
                <Typography variant={"label"} children={"Priority"}></Typography>
                <Typography variant={"label"} showBold children={plan.priority || "-"}></Typography>
              </Grid>
              <Grid
                item
                xs={true}
                paddingX="12px"
                paddingY={"8px"}
                display="flex"
                justifyContent={"center"}
                alignItems="center"
                flexDirection={"column"}
                sx={{ backgroundColor: "#F7F8F9" }}
              >
                <Typography variant={"label"} children={"Due Date"}></Typography>
                <Typography
                  variant={"label"}
                  showBold
                  children={plan.dueDate ? getUIFormatDate(plan.dueDate) : "-"}
                ></Typography>
              </Grid>
              <Grid
                item
                xs={6}
                paddingX="12px"
                paddingY={"8px"}
                display="flex"
                justifyContent={"center"}
                alignItems="center"
                flexDirection={"column"}
                sx={{ backgroundColor: "#F7F8F9" }}
              >
                <Typography variant={"label"} children={"Status"}></Typography>
                <Typography variant={"label"} showBold children={plan.status}></Typography>
              </Grid>
              <Grid
                item
                xs={true}
                paddingX="38px"
                paddingY={"20px"}
                alignItems="center"
                sx={{ backgroundColor: "#F7F8F9" }}
              >
                {getlinearProgress()}
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid item md={4}>
              {getPlanTypeLabelElement()}
              <Box margin={"10px 0px"}>
                <Typography variant={"h5"} color={tokens.Text.General.PrimaryHeadline} showBold>
                  {plan.title || "-"}
                </Typography>
              </Box>
              <Chip label={plan.innCode} />
              <Box marginTop={"4px"}>
                {getPlanOwners()} {getPlanSource()}
              </Box>
            </Grid>
            <Grid item md={2} height={"100%"}>
              <FunctionalAreaChips plan={plan} />
            </Grid>
            <Grid container item md={6} alignItems={"center"}>
              <Grid item md={2}>
                <Typography variant="body2" showBold color={tokens.Text.General.PrimaryBody}>
                  {plan.priority || "-"}
                </Typography>
              </Grid>
              <Grid item md={3}>
                <Typography variant="body2" color={tokens.Text.General.PrimaryBody}>
                  {plan.dueDate ? getUIFormatDate(plan.dueDate) : "-"}
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="body2" showBold color={tokens.Text.General.PrimaryBody}>
                  {plan.status}
                </Typography>
                {getlinearProgress()}
              </Grid>
              <Grid item display="flex" alignItems={"center"} justifyContent="space-around" marginLeft={"auto"}>
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/myplandetails/${plan?.id}`)}
                  label="View Details"
                />
                <Box marginLeft={"10px"}>
                  <KebabMenu options={SectionMenu} />
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </PlanCard>

      {openEditPlan && <Edit openEditPlan={openEditPlan} setOpenEditPlan={setOpenEditPlan} />}
      {openDeleteCapthaDialog && (
        <Delete
          openDeleteCapthaDialog={openDeleteCapthaDialog}
          setOpenDeleteCapthaDialog={setOpenDeleteCapthaDialog}
          plan={plan}
        />
      )}
    </>
  );
}

export default Item;
